/**
 * AdminServicesPage - Vista administrativa de servicios de todos los clientes
 *
 * Funcionalidades:
 * - Carga servicios con adminService.getServices(tenantId=null)
 * - Tabla con filtros por cliente, estado, tipo
 * - Buscar por nombre
 * - Click en servicio abre AdminServiceDetailPage
 * - Botones de acción: Parar, Eliminar (con confirmación)
 * - Estados de loading/error
 * - Paginación
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  DeleteOutlined,
  PauseOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { Modal, message, Tag, Tooltip, Spin } from 'antd';
import { adminService, Service, Client } from '../../services/admin.service';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import { useNotification } from '../../hooks/useNotification';
import { safeDate } from '../../utils/formatters';

const StatusColors: Record<string, string> = {
  'RUNNING': 'green',
  'CREATING': 'blue',
  'STOPPED': 'volcano',
  'TERMINATED': 'red',
  'ERROR': 'red',
  'FAILED': 'red',
  'DELETING': 'orange',
  'DELETED': 'default',
};

const StatusLabels: Record<string, string> = {
  'RUNNING': 'Ejecutándose',
  'CREATING': 'Creando',
  'STOPPED': 'Detenido',
  'TERMINATED': 'Terminado',
  'ERROR': 'Error',
  'FAILED': 'Fallido',
  'DELETING': 'Eliminando',
  'DELETED': 'Eliminado',
};

const TypeLabels: Record<string, string> = {
  'APP_DEPLOY_STATIC': 'Sitio Estático',
  'APP_DEPLOY_SERVER': 'App Servidor',
  'COMPUTE': 'VM',
  'DATABASE': 'Base de Datos',
  'CLICKHOUSE': 'ClickHouse',
  'FRAPPE_EDUCATION': 'Frappe',
  'IDURAR': 'IDURAR',
  'N8N': 'n8n',
  'GHOST': 'Ghost',
  'STRAPI': 'Strapi',
  'ODOO': 'Odoo',
  'METABASE': 'Metabase',
  'RABBITMQ': 'RabbitMQ',
  'REDIS': 'Redis',
  'ONYX': 'Onyx',
  'DOCKER': 'Docker',
  'MEDUSA': 'Medusa',
  'TOOLJET': 'ToolJet',
};

interface ServiceWithTenant extends Service {
  tenantName?: string;
}

interface LoadingState {
  isLoading: boolean;
  error: string | null;
  isStoppingServiceId?: string;
  isDeletingServiceId?: string;
}

const AdminServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useNotification();

  // Estado de datos
  const [services, setServices] = useState<ServiceWithTenant[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: true,
    error: null,
  });

  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClientId, setFilterClientId] = useState<string | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<string | 'all'>('all');
  const [filterType, setFilterType] = useState<string | 'all'>('all');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /**
   * Carga servicios de todos los clientes (tenantId=null)
   */
  const loadServices = useCallback(async () => {
    setLoading({ isLoading: true, error: null });
    try {
      const result = await adminService.getServices({ tenantId: undefined });

      if (result.error) {
        setLoading({
          isLoading: false,
          error: result.error as string,
        });
        setServices([]);
        return;
      }

      setServices(result.services || []);
      setLoading({ isLoading: false, error: null });
    } catch (error: any) {
      setLoading({
        isLoading: false,
        error: error?.message || 'Error al cargar servicios',
      });
      setServices([]);
    }
  }, []);

  /**
   * Carga lista de clientes para el dropdown
   */
  const loadClients = useCallback(async () => {
    try {
      const result = await adminService.getClients({ limit: 100 });

      if (result.error) {
        console.error('Error loading clients:', result.error);
        return;
      }

      setClients(result.clients || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  }, []);

  // Cargar datos al montar
  useEffect(() => {
    loadServices();
    loadClients();
  }, [loadServices, loadClients]);

  /**
   * Filtra servicios por búsqueda, cliente, estado y tipo
   */
  const filteredServices = services.filter((service) => {
    const matchesSearch = (service.name ?? '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesClient =
      filterClientId === 'all' || service.tenantId === filterClientId;

    const matchesStatus =
      filterStatus === 'all' || service.status === filterStatus;

    const matchesType =
      filterType === 'all' || service.type === filterType;

    return matchesSearch && matchesClient && matchesStatus && matchesType;
  });

  /**
   * Paginación
   */
  const paginatedServices = filteredServices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

  /**
   * Maneja detención de servicio con confirmación
   */
  const handleStopService = (service: ServiceWithTenant) => {
    Modal.confirm({
      title: 'Detener servicio',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>¿Está seguro que desea detener este servicio?</p>
          <p className="font-semibold mt-2">{service.name}</p>
          <p className="text-sm text-gray-600 mt-1">
            El servicio será detenido pero no eliminado.
          </p>
        </div>
      ),
      okText: 'Detener',
      okType: 'primary',
      cancelText: 'Cancelar',
      onOk: async () => {
        setLoading(prev => ({ ...prev, isStoppingServiceId: service.serviceId }));
        try {
          const result = await adminService.stopService(service.serviceId);

          if (result.success) {
            message.success('Servicio detenido correctamente');
            // Actualizar el estado del servicio en la lista
            setServices(prev =>
              prev.map(s =>
                s.serviceId === service.serviceId
                  ? { ...s, status: 'STOPPED' }
                  : s
              )
            );
          } else {
            message.error(result.error || 'Error al detener el servicio');
          }
        } catch (error: any) {
          message.error(error?.message || 'Error al detener el servicio');
        } finally {
          setLoading(prev => ({ ...prev, isStoppingServiceId: undefined }));
        }
      },
    });
  };

  /**
   * Maneja eliminación de servicio con confirmación
   */
  const handleDeleteService = (service: ServiceWithTenant) => {
    Modal.confirm({
      title: 'Eliminar servicio',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2">
            <AlertOutlined className="text-red-600 mt-0.5" />
            <div className="text-sm text-red-700">
              Esta acción es irreversible. Se eliminarán todos los datos asociados.
            </div>
          </div>
          <p>¿Está seguro que desea eliminar este servicio?</p>
          <p className="font-semibold mt-2">{service.name}</p>
          <p className="text-sm text-gray-600 mt-1">
            Cliente: {service.tenantId}
          </p>
        </div>
      ),
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        setLoading(prev => ({ ...prev, isDeletingServiceId: service.serviceId }));
        try {
          const result = await adminService.deleteService(service.serviceId);

          if (result.success) {
            message.success('Servicio eliminado correctamente');
            // Remover servicio de la lista
            setServices(prev =>
              prev.filter(s => s.serviceId !== service.serviceId)
            );
          } else {
            message.error(result.error || 'Error al eliminar el servicio');
          }
        } catch (error: any) {
          message.error(error?.message || 'Error al eliminar el servicio');
        } finally {
          setLoading(prev => ({ ...prev, isDeletingServiceId: undefined }));
        }
      },
    });
  };

  /**
   * Navega al detalle del servicio
   */
  const handleViewDetail = (service: ServiceWithTenant) => {
    navigate(`/admin/services/${service.serviceId}`, {
      state: { service, tenantId: service.tenantId },
    });
  };

  /**
   * Obtiene el nombre del cliente por tenantId
   */
  const getClientName = (tenantId: string): string => {
    const client = clients.find(c => c.tenantId === tenantId);
    return client?.tenantName || tenantId;
  };

  /**
   * Renderiza estado de loading
   */
  if (loading.isLoading) {
    return (
      <div className="max-w-[1600px] mx-auto px-8 py-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Cargando servicios...
          </p>
        </div>
      </div>
    );
  }

  /**
   * Renderiza error
   */
  if (loading.error) {
    return (
      <div className="max-w-[1600px] mx-auto px-8 py-6">
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertOutlined className="text-red-600 dark:text-red-400 text-xl mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-100">
                Error al cargar servicios
              </h3>
              <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                {loading.error}
              </p>
              <button
                onClick={loadServices}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Servicios
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Gestión centralizada de servicios de todos los clientes
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadServices}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ReloadOutlined />
              Actualizar
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Buscar */}
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre de servicio..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Filtro Cliente */}
          <div className="flex items-center gap-2">
            <FilterOutlined className="text-gray-400" />
            <select
              value={filterClientId}
              onChange={(e) => {
                setFilterClientId(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="all">Todos los clientes</option>
              {clients.map((client) => (
                <option key={client.tenantId} value={client.tenantId}>
                  {client.tenantName}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro Estado */}
          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            <option value="all">Todos los estados</option>
            <option value="RUNNING">Ejecutándose</option>
            <option value="CREATING">Creando</option>
            <option value="STOPPED">Detenido</option>
            <option value="ERROR">Error</option>
            <option value="FAILED">Fallido</option>
            <option value="TERMINATED">Terminado</option>
            <option value="DELETING">Eliminando</option>
            <option value="DELETED">Eliminado</option>
          </select>

          {/* Filtro Tipo */}
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            <option value="all">Todos los tipos</option>
            <option value="APP_DEPLOY_STATIC">Sitio Estático</option>
            <option value="APP_DEPLOY_SERVER">App Servidor</option>
            <option value="COMPUTE">Máquina Virtual</option>
            <option value="DATABASE">Base de Datos</option>
            <option value="CLICKHOUSE">ClickHouse</option>
            <option value="FRAPPE_EDUCATION">Frappe Education</option>
            <option value="IDURAR">IDURAR</option>
            <option value="N8N">n8n</option>
            <option value="GHOST">Ghost</option>
            <option value="STRAPI">Strapi</option>
            <option value="ODOO">Odoo</option>
            <option value="METABASE">Metabase</option>
            <option value="RABBITMQ">RabbitMQ</option>
            <option value="REDIS">Redis</option>
            <option value="ONYX">Onyx</option>
            <option value="DOCKER">Docker</option>
            <option value="MEDUSA">Medusa</option>
            <option value="TOOLJET">ToolJet</option>
          </select>
        </div>
      </div>

      {/* Services Table */}
      {filteredServices.length === 0 ? (
        <EmptyState
          title="No se encontraron servicios"
          description={
            searchTerm || filterClientId !== 'all' || filterStatus !== 'all' || filterType !== 'all'
              ? 'Intenta ajustar los filtros de búsqueda.'
              : 'No hay servicios registrados en el sistema.'
          }
        />
      ) : (
        <>
          {/* Tabla */}
          <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Costo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Creado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedServices.map((service) => (
                  <tr
                    key={service.serviceId}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {/* Nombre */}
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleViewDetail(service)}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-sm"
                      >
                        {service.name}
                      </button>
                    </td>

                    {/* Cliente */}
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {getClientName(service.tenantId)}
                      </span>
                    </td>

                    {/* Tipo */}
                    <td className="px-6 py-4">
                      <Tag color="blue" className="text-xs">
                        {TypeLabels[service.type] || service.type || '—'}
                      </Tag>
                    </td>

                    {/* Estado */}
                    <td className="px-6 py-4">
                      <Tag
                        color={StatusColors[service.status] || 'default'}
                        className="text-xs"
                      >
                        {StatusLabels[service.status] || service.status}
                      </Tag>
                    </td>

                    {/* Costo */}
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        ${service.cost?.toFixed(2) || '0.00'}/mes
                      </span>
                    </td>

                    {/* Creado */}
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {safeDate(service.createdAt)}
                      </span>
                    </td>

                    {/* Acciones */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {/* Botón Ver Detalles */}
                        <Tooltip title="Ver detalles">
                          <button
                            onClick={() => handleViewDetail(service)}
                            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded transition-colors"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                        </Tooltip>

                        {/* Botón Parar (solo si está ejecutándose) */}
                        {service.status === 'RUNNING' && (
                          <Tooltip title="Detener servicio">
                            <button
                              onClick={() => handleStopService(service)}
                              disabled={loading.isStoppingServiceId === service.serviceId}
                              className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loading.isStoppingServiceId === service.serviceId ? (
                                <LoadingOutlined className="animate-spin" />
                              ) : (
                                <PauseOutlined />
                              )}
                            </button>
                          </Tooltip>
                        )}

                        {/* Botón Eliminar */}
                        <Tooltip title="Eliminar servicio">
                          <button
                            onClick={() => handleDeleteService(service)}
                            disabled={loading.isDeletingServiceId === service.serviceId}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading.isDeletingServiceId === service.serviceId ? (
                              <LoadingOutlined className="animate-spin" />
                            ) : (
                              <DeleteOutlined />
                            )}
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
                {Math.min(currentPage * itemsPerPage, filteredServices.length)} de{' '}
                {filteredServices.length} servicios
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Total Servicios
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {filteredServices.length}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Ejecutándose
              </div>
              <div className="text-2xl font-bold text-green-600">
                {filteredServices.filter(s => s.status === 'RUNNING').length}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Detenidos
              </div>
              <div className="text-2xl font-bold text-orange-600">
                {filteredServices.filter(s => s.status === 'STOPPED').length}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Con Error
              </div>
              <div className="text-2xl font-bold text-red-600">
                {filteredServices.filter(s => s.status === 'ERROR').length}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminServicesPage;
